import { useCallback, useRef } from "react";

import { ChildMessagesStore } from "@/components/Playground/Messages/PlaceholderContainer/stores/child-messages-store";
import { usePlayground } from "@/components/Playground/playground-store";

/**
 * Hook to manage a Map of ChildMessagesStores, one per input variable set.
 * Provides lazy creation, cleanup, and persistence across renders.
 */
export const useChildStoresMap = () => {
  const playground = usePlayground();
  const storesMapRef = useRef<Map<string, ChildMessagesStore>>(new Map());

  /**
   * Gets an existing store or creates a new one for the given set ID.
   */
  const getOrCreateStore = useCallback((setId: string): ChildMessagesStore => {
    if (!storesMapRef.current.has(setId)) {
      storesMapRef.current.set(setId, new ChildMessagesStore());
    }
    return storesMapRef.current.get(setId)!;
  }, []);

  /**
   * Removes stores for sets that no longer exist.
   */
  const cleanupStaleStores = useCallback(() => {
    const validSetIds = new Set(playground.input_variable_sets.map(s => s.id));
    for (const setId of storesMapRef.current.keys()) {
      if (!validSetIds.has(setId)) {
        storesMapRef.current.delete(setId);
      }
    }
  }, [playground.input_variable_sets]);

  /**
   * Ensures stores exist for all current variable sets.
   * Also cleans up stale stores.
   */
  const initializeStores = useCallback(() => {
    playground.input_variable_sets.forEach(set => {
      getOrCreateStore(set.id);
    });
    cleanupStaleStores();
  }, [playground.input_variable_sets, getOrCreateStore, cleanupStaleStores]);

  return {
    storesMap: storesMapRef.current,
    getOrCreateStore,
    cleanupStaleStores,
    initializeStores,
  };
};
